import { useLocation } from '@hulu/react-router-dom';
import { useEffect, useRef } from 'react';

import { DCM_AMPLITUDE_ENABLED_FLAG } from '../../../constants/launchDarklyFlags';
import useLaunchDarklyFlag from '../../featureFlags/useLaunchDarkly';
import { normalizeAnalyticsPagePath } from '../utils';
import { endPageSession, reconcilePageSession, setAmplitudeTrackingEnabled } from './analyticsClient';
import { processRageClickEvent } from './rageClickTracking';
import useAnalyticsUserIdentification from './useAnalyticsUserIdentification';

type Props = {
  children: React.ReactNode;
};

const AmplitudeProvider = ({ children }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const { value: enabled, loading } = useLaunchDarklyFlag(DCM_AMPLITUDE_ENABLED_FLAG, false);
  const isTrackingActive = enabled && !loading;

  useAnalyticsUserIdentification(isTrackingActive);

  useEffect(() => {
    if (loading) {
      return;
    }

    setAmplitudeTrackingEnabled(enabled);
  }, [enabled, loading]);

  useEffect(() => {
    if (!isTrackingActive) {
      return;
    }

    const clickHistory = new Map<string, number[]>();

    const handleClick = (event: MouseEvent): void => {
      processRageClickEvent(event, clickHistory);
    };

    document.addEventListener('click', handleClick, true);

    return () => document.removeEventListener('click', handleClick, true);
  }, [isTrackingActive]);

  useEffect(() => {
    if (!isTrackingActive) {
      if (previousPathRef.current) {
        endPageSession();
        previousPathRef.current = null;
      }
      return;
    }

    const normalizedPathname = normalizeAnalyticsPagePath(pathname);

    if (previousPathRef.current === normalizedPathname) {
      return;
    }

    reconcilePageSession(pathname);
    previousPathRef.current = normalizedPathname;
  }, [isTrackingActive, pathname]);

  useEffect(() => {
    if (!isTrackingActive) {
      return;
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        endPageSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isTrackingActive]);

  return <>{children}</>;
};

export default AmplitudeProvider;
